'use client';

import type {
  Address,
  Cart,
  CartCreateResponse,
  CartLinesAddResponse,
  CartLinesRemoveResponse,
  CartLinesUpdateResponse,
  CartResponse,
  Customer,
  CustomerAccessToken,
  CustomerAccessTokenCreateResponse,
  CustomerCreateResponse,
  CustomerResponse,
  CustomerUserError,
  Order,
  OrderLineItem,
  Product,
  ShopifyCart,
  ShopifyCustomer,
  ShopifyOrder,
  ShopifyProduct,
  ShopifyResponse,
} from './types';

const domain = process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN;
const storefrontAccessToken = process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN;
const checkoutHost = process.env.NEXT_PUBLIC_SHOPIFY_CHECKOUT_HOST || domain;
const apiVersion = '2024-01';

const PRODUCT_FRAGMENT = `
  fragment ProductFragment on Product {
    id
    handle
    title
    description
    descriptionHtml
    availableForSale
    updatedAt
    featuredImage { url altText width height }
    images(first: 10) { edges { node { url altText width height } } }
    options { id name values }
    priceRange {
      minVariantPrice { amount currencyCode }
      maxVariantPrice { amount currencyCode }
    }
    variants(first: 100) {
      edges {
        node {
          id
          title
          sku
          availableForSale
          selectedOptions { name value }
          price { amount currencyCode }
          compareAtPrice { amount currencyCode }
          image { url altText width height }
        }
      }
    }
    tags
    productType
    vendor
    seo { title description }
  }
`;

const CART_FRAGMENT = `
  fragment CartFragment on Cart {
    id
    checkoutUrl
    cost {
      subtotalAmount { amount currencyCode }
      totalAmount { amount currencyCode }
      totalTaxAmount { amount currencyCode }
    }
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          attributes { key value }
          cost { totalAmount { amount currencyCode } }
          merchandise {
            ... on ProductVariant {
              id
              title
              selectedOptions { name value }
              product {
                id
                handle
                title
                featuredImage { url altText width height }
              }
              price { amount currencyCode }
            }
          }
        }
      }
    }
    totalQuantity
  }
`;

const ADDRESS_FRAGMENT = `
  fragment AddressFragment on MailingAddress {
    id
    firstName
    lastName
    company
    address1
    address2
    city
    province
    provinceCode
    country
    countryCodeV2
    zip
    phone
    formatted
  }
`;

const ORDER_FRAGMENT = `
  fragment OrderFragment on Order {
    id
    orderNumber
    name
    processedAt
    fulfillmentStatus
    financialStatus
    currentTotalPrice { amount currencyCode }
    statusUrl
    shippingAddress { ...AddressFragment }
    lineItems(first: 50) {
      edges {
        node {
          title
          quantity
          variant {
            id
            title
            price { amount currencyCode }
            image { url altText width height }
            product { handle }
          }
        }
      }
    }
  }
`;

const CUSTOMER_FRAGMENT = `
  fragment CustomerFragment on Customer {
    id
    email
    firstName
    lastName
    displayName
    phone
    acceptsMarketing
    createdAt
    defaultAddress { ...AddressFragment }
    addresses(first: 10) { edges { node { ...AddressFragment } } }
    orders(first: 20, sortKey: PROCESSED_AT, reverse: true) {
      edges { node { ...OrderFragment } }
    }
  }
`;

async function storefrontFetch<T>(
  query: string,
  variables?: Record<string, unknown>,
  signal?: AbortSignal
): Promise<T> {
  if (!domain || !storefrontAccessToken) {
    throw new Error('Missing public Shopify Storefront configuration.');
  }

  const response = await fetch(`https://${domain}/api/${apiVersion}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
    },
    body: JSON.stringify({ query, variables }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Shopify API error: ${response.status} ${response.statusText}`);
  }

  const json: ShopifyResponse<T> = await response.json();
  if (json.errors?.length) {
    throw new Error(json.errors[0].message);
  }

  return json.data;
}

function rewriteCheckoutUrl(url: string): string {
  if (!checkoutHost) return url;
  try {
    const next = new URL(url);
    next.hostname = checkoutHost;
    return next.toString();
  } catch {
    return url;
  }
}

function reshapeProduct(product: ShopifyProduct): Product {
  return {
    ...product,
    images: product.images.edges.map((edge) => edge.node),
    variants: product.variants.edges.map((edge) => edge.node),
  };
}

function reshapeCart(cart: ShopifyCart): Cart {
  return {
    ...cart,
    checkoutUrl: rewriteCheckoutUrl(cart.checkoutUrl),
    lines: cart.lines.edges.map((edge) => edge.node),
  };
}

function reshapeOrder(order: ShopifyOrder): Order {
  return {
    ...order,
    lineItems: order.lineItems.edges.map((edge) => edge.node) as OrderLineItem[],
  };
}

function reshapeCustomer(customer: ShopifyCustomer): Customer {
  return {
    ...customer,
    addresses: customer.addresses.edges.map((edge) => edge.node) as Address[],
    orders: customer.orders.edges.map((edge) => reshapeOrder(edge.node)) as Order[],
  };
}

function firstUserError(errors: CustomerUserError[] | undefined): string | null {
  if (!errors || errors.length === 0) return null;
  return errors[0].message;
}

export async function createCartClient(): Promise<Cart> {
  const data = await storefrontFetch<CartCreateResponse>(`
    ${CART_FRAGMENT}
    mutation CreateCart {
      cartCreate {
        cart { ...CartFragment }
        userErrors { field message }
      }
    }
  `);

  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors[0].message);
  }

  return reshapeCart(data.cartCreate.cart);
}

export async function getCartClient(cartId: string): Promise<Cart | null> {
  const data = await storefrontFetch<CartResponse>(
    `
      ${CART_FRAGMENT}
      query GetCart($cartId: ID!) {
        cart(id: $cartId) { ...CartFragment }
      }
    `,
    { cartId }
  );

  return data.cart ? reshapeCart(data.cart) : null;
}

export async function addToCartClient(
  cartId: string,
  lines: { merchandiseId: string; quantity: number; attributes?: { key: string; value: string }[] }[]
): Promise<Cart> {
  const data = await storefrontFetch<CartLinesAddResponse>(
    `
      ${CART_FRAGMENT}
      mutation AddToCart($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart { ...CartFragment }
          userErrors { field message }
        }
      }
    `,
    { cartId, lines }
  );

  if (data.cartLinesAdd.userErrors.length > 0) {
    throw new Error(data.cartLinesAdd.userErrors[0].message);
  }

  return reshapeCart(data.cartLinesAdd.cart);
}

export async function updateCartClient(
  cartId: string,
  lines: { id: string; quantity: number }[]
): Promise<Cart> {
  const data = await storefrontFetch<CartLinesUpdateResponse>(
    `
      ${CART_FRAGMENT}
      mutation UpdateCart($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart { ...CartFragment }
          userErrors { field message }
        }
      }
    `,
    { cartId, lines }
  );

  if (data.cartLinesUpdate.userErrors.length > 0) {
    throw new Error(data.cartLinesUpdate.userErrors[0].message);
  }

  return reshapeCart(data.cartLinesUpdate.cart);
}

export async function removeFromCartClient(cartId: string, lineIds: string[]): Promise<Cart> {
  const data = await storefrontFetch<CartLinesRemoveResponse>(
    `
      ${CART_FRAGMENT}
      mutation RemoveFromCart($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart { ...CartFragment }
          userErrors { field message }
        }
      }
    `,
    { cartId, lineIds }
  );

  if (data.cartLinesRemove.userErrors.length > 0) {
    throw new Error(data.cartLinesRemove.userErrors[0].message);
  }

  return reshapeCart(data.cartLinesRemove.cart);
}

export async function predictiveSearchClient(
  searchQuery: string,
  limit: number = 6,
  signal?: AbortSignal
): Promise<Product[]> {
  const data = await storefrontFetch<{
    predictiveSearch: { products: ShopifyProduct[] };
  }>(
    `
      ${PRODUCT_FRAGMENT}
      query PredictiveSearch($query: String!, $limit: Int!) {
        predictiveSearch(query: $query, limit: $limit, types: PRODUCT) {
          products { ...ProductFragment }
        }
      }
    `,
    { query: searchQuery, limit },
    signal
  );

  return data.predictiveSearch.products.map(reshapeProduct);
}

export async function searchProductsClient(
  searchQuery: string,
  first: number = 50
): Promise<Product[]> {
  const data = await storefrontFetch<{
    search: { edges: { node: ShopifyProduct }[] };
  }>(
    `
      ${PRODUCT_FRAGMENT}
      query SearchProducts($query: String!, $first: Int!) {
        search(query: $query, first: $first, types: PRODUCT) {
          edges {
            node {
              ... on Product { ...ProductFragment }
            }
          }
        }
      }
    `,
    { query: searchQuery, first }
  );

  return data.search.edges.map((edge) => reshapeProduct(edge.node));
}

export async function customerCreateClient(input: {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  acceptsMarketing?: boolean;
}): Promise<{ success: boolean; error: string | null }> {
  const data = await storefrontFetch<CustomerCreateResponse>(
    `
      mutation CustomerCreate($input: CustomerCreateInput!) {
        customerCreate(input: $input) {
          customer { id email }
          customerUserErrors { code field message }
        }
      }
    `,
    { input }
  );

  const err = firstUserError(data.customerCreate.customerUserErrors);
  return { success: !err && !!data.customerCreate.customer, error: err };
}

export async function customerAccessTokenCreateClient(
  email: string,
  password: string
): Promise<{ token: CustomerAccessToken | null; error: string | null }> {
  const data = await storefrontFetch<CustomerAccessTokenCreateResponse>(
    `
      mutation CustomerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
        customerAccessTokenCreate(input: $input) {
          customerAccessToken { accessToken expiresAt }
          customerUserErrors { code field message }
        }
      }
    `,
    { input: { email, password } }
  );

  return {
    token: data.customerAccessTokenCreate.customerAccessToken,
    error: firstUserError(data.customerAccessTokenCreate.customerUserErrors),
  };
}

export async function customerAccessTokenDeleteClient(token: string): Promise<void> {
  await storefrontFetch(
    `
      mutation CustomerAccessTokenDelete($customerAccessToken: String!) {
        customerAccessTokenDelete(customerAccessToken: $customerAccessToken) {
          deletedAccessToken
          userErrors { field message }
        }
      }
    `,
    { customerAccessToken: token }
  );
}

export async function customerRecoverClient(email: string): Promise<void> {
  await storefrontFetch(
    `
      mutation CustomerRecover($email: String!) {
        customerRecover(email: $email) {
          customerUserErrors { code field message }
        }
      }
    `,
    { email }
  );
}

export async function customerResetByUrlClient(
  resetUrl: string,
  password: string
): Promise<{ token: CustomerAccessToken | null; error: string | null }> {
  const data = await storefrontFetch<{
    customerResetByUrl: {
      customerAccessToken: CustomerAccessToken | null;
      customerUserErrors: CustomerUserError[];
    };
  }>(
    `
      mutation CustomerResetByUrl($resetUrl: URL!, $password: String!) {
        customerResetByUrl(resetUrl: $resetUrl, password: $password) {
          customerAccessToken { accessToken expiresAt }
          customerUserErrors { code field message }
        }
      }
    `,
    { resetUrl, password }
  );

  return {
    token: data.customerResetByUrl.customerAccessToken,
    error: firstUserError(data.customerResetByUrl.customerUserErrors),
  };
}

export async function getCustomerClient(accessToken: string): Promise<Customer | null> {
  try {
    const data = await storefrontFetch<CustomerResponse>(
      `
        ${ADDRESS_FRAGMENT}
        ${ORDER_FRAGMENT}
        ${CUSTOMER_FRAGMENT}
        query GetCustomer($customerAccessToken: String!) {
          customer(customerAccessToken: $customerAccessToken) {
            ...CustomerFragment
          }
        }
      `,
      { customerAccessToken: accessToken }
    );

    return data.customer ? reshapeCustomer(data.customer) : null;
  } catch {
    return null;
  }
}
